import { ApiClient } from "@/services/api/client";
import { queryClient } from "@/lib/react-query";
import { logger } from "@/utils";

/**
 * Service Integration Registry
 *
 * Centralized registry for all backend service integrations.
 * Provides unified access patterns and caching strategies.
 */

export interface ServiceConfig {
  name: string;
  baseEndpoint: string;
  cacheStrategy: "real-time" | "stable" | "static" | "background";
  rateLimit?: {
    windowMs: number;
    maxRequests: number;
  };
  authentication: "required" | "optional" | "none";
  retryPolicy?: {
    maxRetries: number;
    backoffMs: number;
  };
  offlineCapable: boolean;
  realTimeEvents?: string[];
}

export interface ServiceOperation<TInput = any, TOutput = any> {
  endpoint: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  cacheKey?: (input: TInput) => string[];
  transform?: (data: any) => TOutput;
  invalidateCache?: string[][];
  optimisticUpdate?: boolean;
  realTimeSync?: boolean;
}

export class ServiceRegistry {
  private static instance: ServiceRegistry;
  private services: Map<string, ServiceConfig> = new Map();
  private operations: Map<string, ServiceOperation> = new Map();
  private apiClient: ApiClient;

  private constructor(apiClient: ApiClient) {
    this.apiClient = apiClient;
    this.initializeServices();
  }

  public static getInstance(apiClient?: ApiClient): ServiceRegistry {
    if (!ServiceRegistry.instance) {
      if (!apiClient) {
        throw new Error(
          "ApiClient required for ServiceRegistry initialization",
        );
      }
      ServiceRegistry.instance = new ServiceRegistry(apiClient);
    }
    return ServiceRegistry.instance;
  }

  private initializeServices(): void {
    // Core Services
    this.registerService("auth", {
      name: "Authentication Service",
      baseEndpoint: "/auth",
      cacheStrategy: "stable",
      authentication: "none",
      offlineCapable: false,
      realTimeEvents: ["auth-changed"],
    });

    this.registerService("users", {
      name: "User Management Service",
      baseEndpoint: "/users",
      cacheStrategy: "stable",
      authentication: "required",
      offlineCapable: true,
      realTimeEvents: ["user-updated", "user-presence"],
    });

    this.registerService("projects", {
      name: "Project Management Service",
      baseEndpoint: "/projects",
      cacheStrategy: "background",
      authentication: "required",
      offlineCapable: true,
      realTimeEvents: ["project-updated", "project-shared"],
    });

    // Collaboration Services
    this.registerService("collaboration", {
      name: "Collaboration Service",
      baseEndpoint: "/collaboration",
      cacheStrategy: "real-time",
      authentication: "required",
      offlineCapable: false,
      realTimeEvents: ["operation", "cursor-update", "presence-update"],
    });

    this.registerService("comments", {
      name: "Comments Service",
      baseEndpoint: "/comments",
      cacheStrategy: "background",
      authentication: "required",
      offlineCapable: true,
      realTimeEvents: ["comment-created", "comment-updated"],
    });

    this.registerService("annotations", {
      name: "Annotations Service",
      baseEndpoint: "/annotations",
      cacheStrategy: "background",
      authentication: "required",
      offlineCapable: true,
      realTimeEvents: ["annotation-created", "annotation-updated"],
    });

    // AI Services
    this.registerService("ai-generation", {
      name: "AI Generation Service",
      baseEndpoint: "/ai/generation",
      cacheStrategy: "static",
      authentication: "required",
      offlineCapable: false,
      rateLimit: { windowMs: 60000, maxRequests: 20 },
    });

    this.registerService("ai-analytics", {
      name: "AI Analytics Service",
      baseEndpoint: "/ai/analytics",
      cacheStrategy: "background",
      authentication: "required",
      offlineCapable: false,
    });

    this.registerService("ai-streaming", {
      name: "AI Streaming Service",
      baseEndpoint: "/ai/streaming",
      cacheStrategy: "real-time",
      authentication: "required",
      offlineCapable: false,
      realTimeEvents: ["ai-stream", "ai-progress"],
    });

    // Agricultural Services
    this.registerService("agricultural-collaboration", {
      name: "Agricultural Collaboration Service",
      baseEndpoint: "/agricultural/collaboration",
      cacheStrategy: "real-time",
      authentication: "required",
      offlineCapable: true,
      realTimeEvents: [
        "weather-alert",
        "equipment-status",
        "expertise-request",
      ],
    });

    this.registerService("agricultural-data", {
      name: "Agricultural Data Service",
      baseEndpoint: "/agricultural/data",
      cacheStrategy: "background",
      authentication: "required",
      offlineCapable: true,
    });

    // Monitoring & Performance Services
    this.registerService("monitoring", {
      name: "Monitoring Service",
      baseEndpoint: "/monitoring",
      cacheStrategy: "real-time",
      authentication: "required",
      offlineCapable: false,
    });

    this.registerService("analytics", {
      name: "Analytics Service",
      baseEndpoint: "/analytics",
      cacheStrategy: "background",
      authentication: "required",
      offlineCapable: false,
    });

    // Storage & Media Services
    this.registerService("storage", {
      name: "Cloud Storage Service",
      baseEndpoint: "/storage",
      cacheStrategy: "static",
      authentication: "required",
      offlineCapable: false,
    });

    this.registerService("assets", {
      name: "Asset Management Service",
      baseEndpoint: "/assets",
      cacheStrategy: "stable",
      authentication: "required",
      offlineCapable: true,
    });

    // Security Services
    this.registerService("security", {
      name: "Security Monitoring Service",
      baseEndpoint: "/security",
      cacheStrategy: "real-time",
      authentication: "required",
      offlineCapable: false,
      realTimeEvents: ["security-alert", "access-violation"],
    });

    // Marketplace Services
    this.registerService("marketplace", {
      name: "Marketplace Service",
      baseEndpoint: "/marketplace",
      cacheStrategy: "stable",
      authentication: "optional",
      offlineCapable: true,
    });

    this.registerService("marketplace-analytics", {
      name: "Marketplace Analytics Service",
      baseEndpoint: "/marketplace/analytics",
      cacheStrategy: "background",
      authentication: "required",
      offlineCapable: false,
    });

    // Database & Cache Services
    this.registerService("database", {
      name: "Database Management Service",
      baseEndpoint: "/database",
      cacheStrategy: "stable",
      authentication: "required",
      offlineCapable: false,
    });

    this.registerService("cache", {
      name: "Intelligent Cache Service",
      baseEndpoint: "/cache",
      cacheStrategy: "real-time",
      authentication: "required",
      offlineCapable: false,
    });

    // Job Processing Services
    this.registerService("jobs", {
      name: "Job Processing Service",
      baseEndpoint: "/jobs",
      cacheStrategy: "background",
      authentication: "required",
      offlineCapable: false,
      realTimeEvents: ["job-progress", "job-completed"],
    });

    // Backup & Migration Services
    this.registerService("backup", {
      name: "Backup Service",
      baseEndpoint: "/backup",
      cacheStrategy: "stable",
      authentication: "required",
      offlineCapable: false,
    });

    this.registerService("migration", {
      name: "Migration Service",
      baseEndpoint: "/migration",
      cacheStrategy: "stable",
      authentication: "required",
      offlineCapable: false,
    });

    // Rate Limiting & Performance Services
    this.registerService("rate-limiting", {
      name: "Advanced Rate Limiting Service",
      baseEndpoint: "/rate-limiting",
      cacheStrategy: "real-time",
      authentication: "required",
      offlineCapable: false,
    });

    this.registerService("performance", {
      name: "Performance Optimization Service",
      baseEndpoint: "/performance",
      cacheStrategy: "background",
      authentication: "required",
      offlineCapable: false,
    });

    logger.info(
      `Initialized ${this.services.size} services in ServiceRegistry`,
    );
  }

  public registerService(serviceId: string, config: ServiceConfig): void {
    this.services.set(serviceId, config);
    logger.debug(`Registered service: ${serviceId}`, { config });
  }

  public getService(serviceId: string): ServiceConfig | undefined {
    return this.services.get(serviceId);
  }

  public getAllServices(): Map<string, ServiceConfig> {
    return new Map(this.services);
  }

  public registerOperation<TInput = any, TOutput = any>(
    operationId: string,
    operation: ServiceOperation<TInput, TOutput>,
  ): void {
    this.operations.set(operationId, operation);
    logger.debug(`Registered operation: ${operationId}`, { operation });
  }

  public getOperation(operationId: string): ServiceOperation | undefined {
    return this.operations.get(operationId);
  }

  public async executeOperation<TInput = any, TOutput = any>(
    operationId: string,
    input?: TInput,
    options?: {
      skipCache?: boolean;
      bypassOffline?: boolean;
    },
  ): Promise<TOutput> {
    const operation = this.operations.get(operationId);
    if (!operation) {
      throw new Error(`Operation not found: ${operationId}`);
    }

    try {
      const response = await this.apiClient[
        operation.method.toLowerCase() as keyof ApiClient
      ](operation.endpoint, operation.method === "GET" ? undefined : input);

      const result = operation.transform
        ? operation.transform(response.data)
        : response.data;

      // Handle cache invalidation
      if (operation.invalidateCache) {
        operation.invalidateCache.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }

      return result;
    } catch (error) {
      logger.error(`Operation failed: ${operationId}`, { error, input });
      throw error;
    }
  }

  public getServicesByCapability(
    capability: keyof ServiceConfig,
  ): ServiceConfig[] {
    return Array.from(this.services.values()).filter(
      (service) => service[capability],
    );
  }

  public getOfflineCapableServices(): ServiceConfig[] {
    return this.getServicesByCapability("offlineCapable").filter(
      (s) => s.offlineCapable,
    );
  }

  public getRealTimeServices(): ServiceConfig[] {
    return Array.from(this.services.values()).filter(
      (service) => service.realTimeEvents && service.realTimeEvents.length > 0,
    );
  }

  public getServiceEndpoint(serviceId: string, path: string = ""): string {
    const service = this.services.get(serviceId);
    if (!service) {
      throw new Error(`Service not found: ${serviceId}`);
    }
    return `${service.baseEndpoint}${path}`;
  }

  public validateServiceAccess(serviceId: string, userRole?: string): boolean {
    const service = this.services.get(serviceId);
    if (!service) return false;

    // Basic validation - can be extended with role-based access
    return service.authentication === "none" || !!userRole;
  }

  public getServiceHealth(): Map<string, "healthy" | "degraded" | "unhealthy"> {
    // Mock implementation - would ping actual services
    const health = new Map<string, "healthy" | "degraded" | "unhealthy">();

    this.services.forEach((config, serviceId) => {
      // Default to healthy - real implementation would check service status
      health.set(serviceId, "healthy");
    });

    return health;
  }

  public getServiceMetrics(serviceId: string): {
    requestCount: number;
    errorRate: number;
    averageResponseTime: number;
    cacheHitRate: number;
  } {
    // Mock implementation - would integrate with monitoring service
    return {
      requestCount: Math.floor(Math.random() * 1000),
      errorRate: Math.random() * 0.05, // 0-5% error rate
      averageResponseTime: Math.floor(Math.random() * 200) + 50, // 50-250ms
      cacheHitRate: Math.random() * 0.4 + 0.6, // 60-100% cache hit rate
    };
  }
}

// Export singleton instance getter
export const getServiceRegistry = (apiClient?: ApiClient) =>
  ServiceRegistry.getInstance(apiClient);
